import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// THE PATIENT HEALTH RECORD, doctor side (doc/one-patient-row.md §8, Phase 3).
//
// Seven sections of a person's medical life kept ONCE, on the person, and read
// at every hospital that treats them. This slice is the tenant web's half of
// `/api/health-record`: the read that fills the patient's "Health record" tab,
// the add that a doctor performs from INSIDE an appointment, and the one verb
// a doctor has on somebody else's self-reported line — Verify (POST …/confirm).
// Flag was retired in round 9 (owner, 24 Sep 2026): the server answers 410
// RETIRED and no longer emits the flagged* keys, so nothing here sends or
// renders it.
//
// TWO REFUSALS ARE NOT ERRORS, they are answers, and the UI must be able to
// tell them apart from a broken request:
//   · 403 NOT_LINKED_DOCTOR — this doctor has never had an appointment with
//     this person. The tab shows a calm panel, not a red failure.
//   · 409 POSSIBLE_DUPLICATE — the server believes this event is already on
//     the record and hands back the row it matched, so the form can ask
//     "same event?" instead of quietly growing a second copy.
// Both keep their `code` in the rejected payload for exactly that reason.

// The seven sections, in the order every client renders them (contract).
export const HEALTH_CATEGORIES = [
    'blood_event', 'allergy', 'condition', 'medicine',
    'surgery', 'life_event', 'vaccination',
];

// The categories whose subtype is a CLOSED list server-side; the rest take
// none. Mirrors Backend/utils/healthAccess.js#HEALTH_SUBTYPES — a value that
// is not in the list is refused with 400 INVALID_SUBTYPE, so the form offers
// only these.
export const HEALTH_SUBTYPES = {
    blood_event: ['donation', 'transfusion', 'transplant'],
    life_event: ['childbirth_normal', 'childbirth_csection', 'pregnancy', 'other'],
    medicine: ['prescribed', 'own'],
};

// The `details` fields each category collects. Every one is optional — the
// server stores `details` as free JSONB and validates only that it is an
// object — so this list is about what a doctor is ASKED for, not what is
// allowed. `type` drives the input; 'select' carries its own options.
export const HEALTH_DETAIL_FIELDS = {
    allergy: [
        { name: 'reaction', type: 'text' },
        { name: 'severity', type: 'select', options: ['mild', 'moderate', 'severe'] },
    ],
    condition: [
        { name: 'sinceYear', type: 'text' },
        { name: 'treatingDoctor', type: 'text' },
    ],
    medicine: [
        { name: 'dose', type: 'text' },
        { name: 'frequency', type: 'text' },
        { name: 'reason', type: 'text' },
    ],
    surgery: [
        { name: 'hospital', type: 'text' },
        { name: 'surgeon', type: 'text' },
    ],
    life_event: [{ name: 'childName', type: 'text' }],
    vaccination: [{ name: 'dose', type: 'text' }],
    blood_event: [
        { name: 'place', type: 'text' },
        { name: 'units', type: 'text' },
    ],
};

// "Still ongoing" only means something where an entry can END: a condition is
// resolved, a medicine is stopped. A vaccination has no ongoing state.
export const ONGOING_CATEGORIES = ['condition', 'medicine'];

const serializeError = (error) => {
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
        return {
            code: data.code || null,
            message: data.message || null,
            status: error.response?.status || null,
            // 409 POSSIBLE_DUPLICATE hands the matched row back so the form can
            // show "Already on record — …" instead of dead-ending.
            existing: data.existing || null,
        };
    }
    return { code: null, message: error?.message || 'Request failed', status: null, existing: null };
};

// GET /health-record/:patientId[?appointmentId=<the open visit>]
// → { patient:{patientId,name,bloodGroup}, categories:{…7…},
//     viewer:{role,canWrite,isLinkedDoctor,lastVisitAt} }
// With the open visit named, a doctor's answer carries `viewer.lastVisitAt`
// (their previous visit with this person, the open one excluded) and every
// entry an `isNew` flag — "added since you last saw them", which is what a
// doctor needs first when the patient walks in.
export const fetchHealthRecord = createAsyncThunk(
    'healthRecord/fetch',
    async ({ patientId, appointmentId = null }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const query = appointmentId ? `?appointmentId=${encodeURIComponent(appointmentId)}` : '';
            const res = await axiosInstance.get(`${baseURL}/health-record/${patientId}${query}`);
            return res.data;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// GET /health-record/:patientId/readings[?appointmentId=<the open visit>]
// (check sets, Round A) → { patientId, count, visits:[…], series:[…], viewer }
//
// The person's LIVE check readings, taken at any hospital they are currently
// with, grouped per visit (newest first), plus one `series` per (set, number
// field) for a trend. NOT behind the check_sets feature: a hospital that does
// not use check sets must not hide readings taken elsewhere. Readings are not
// an eighth category — a separate section with its own endpoint; the record's
// own answer carries only `counts.readings` for the chip badge.
export const fetchHealthReadings = createAsyncThunk(
    'healthRecord/fetchReadings',
    async ({ patientId, appointmentId = null }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const query = appointmentId ? `?appointmentId=${encodeURIComponent(appointmentId)}` : '';
            const res = await axiosInstance.get(`${baseURL}/health-record/${patientId}/readings${query}`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// GET /health-record/:patientId/readings?visit=<appointmentId>
// (check sets, owner bug report 26 Sep 2026) → the same answer as above with
// `visits` holding only that appointment's group.
//
// ONE visit's readings, read out of the person's record: what the patient (or
// a family login with a share) sees in an appointment's details, whatever its
// status. Same gate as the record, NOT behind the check_sets feature. Kept in
// its own map, keyed by appointment, so opening a visit never replaces what
// the Readings section of the record holds.
export const fetchVisitRecordReadings = createAsyncThunk(
    'healthRecord/fetchVisitReadings',
    async ({ patientId, appointmentId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(
                `${baseURL}/health-record/${encodeURIComponent(patientId)}/readings?visit=${encodeURIComponent(appointmentId)}`
            );
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    },
    {
        condition: ({ patientId, appointmentId } = {}) => Boolean(patientId && appointmentId),
    }
);

// GET /health-record/:patientId/programmes (care programmes, Round B)
// → { patientId, counts:{active,closed}, active:[…], closed:[…], viewer }
//
// Every programme started for the person at a hospital they are with now,
// each with the timeline the server computes (projection `record`: no user
// ids). NOT behind the care_programmes feature — like readings, a hospital
// that does not run programmes must not hide one started elsewhere. The
// record's own answer carries `counts.programmes` (ACTIVE ones) for the chip.
export const fetchHealthProgrammes = createAsyncThunk(
    'healthRecord/fetchProgrammes',
    async ({ patientId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/health-record/${encodeURIComponent(patientId)}/programmes`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    },
    {
        condition: ({ patientId } = {}) => Boolean(patientId),
    }
);

// POST /health-record/:patientId/entries
//   201 { created:true, entry } · 409 POSSIBLE_DUPLICATE { existing }
//   200 { created:false, entry } (confirmSameOf — the doctor verified the row
//        that was already there, and nothing new exists)
//
// A DOCTOR MUST SEND `appointmentId`: the server refuses without it (400
// APPOINTMENT_REQUIRED), because a doctor writes on somebody's permanent
// record only from inside their own appointment with them.
export const addHealthEntry = createAsyncThunk(
    'healthRecord/addEntry',
    async ({ patientId, ...body }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/health-record/${patientId}/entries`, body);
            return res.data;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// POST /health-record/entries/:entryId/confirm — "Verify": a linked doctor
// stands behind a self-reported line. The row becomes Verified; no second row
// is created.
export const confirmHealthEntry = createAsyncThunk(
    'healthRecord/confirmEntry',
    async ({ entryId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/health-record/entries/${entryId}/confirm`, {});
            return res.data;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

const emptyCategories = () => {
    const out = {};
    HEALTH_CATEGORIES.forEach((c) => { out[c] = []; });
    return out;
};

/** Put a server-returned entry back where it belongs, replacing its old copy. */
const upsertEntry = (state, entry) => {
    if (!entry || !entry.category) return;
    const list = state.categories[entry.category] || (state.categories[entry.category] = []);
    const at = list.findIndex((e) => e.entryId === entry.entryId);
    // Merged, not replaced: a write's answer does not recompute `isNew`
    // (that needs the open visit), and verifying a line must not quietly
    // drop its "New" badge.
    if (at >= 0) list[at] = { ...list[at], ...entry };
    // Newest first inside a section, which is how the server sorts it too.
    else list.unshift(entry);
};

const emptyReadings = () => ({
    patientId: null,
    appointmentId: null,
    visits: [],
    series: [],
    count: 0,
    loading: false,
    loaded: false,
    error: null,
});

const emptyProgrammes = () => ({
    patientId: null,
    active: [],
    closed: [],
    loading: false,
    loaded: false,
    error: null,
});

// The record's chip badges: live readings, ACTIVE programmes. 0 = unknown.
const countOf = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
};
const emptyCounts = () => ({ readings: 0, programmes: 0 });

const healthRecordSlice = createSlice({
    name: 'healthRecord',
    initialState: {
        patient: null,
        categories: emptyCategories(),
        viewer: null,
        // `counts.readings` — the number of live check readings the readings
        // endpoint would return (the Readings chip badge); `counts.programmes`
        // — the ACTIVE programmes the Programmes section would list. 0 when
        // unknown.
        counts: emptyCounts(),
        readings: emptyReadings(),
        // The Programmes section (fetchHealthProgrammes).
        programmes: emptyProgrammes(),
        // appointmentId → { patientId, readings, loading, loaded, error }:
        // one visit's live readings (fetchVisitRecordReadings).
        visitReadings: {},
        loading: false,
        // Carries the server's `code`, so NOT_LINKED_DOCTOR can be told from a
        // genuine failure without matching on a sentence.
        error: null,
        saving: false,
        addError: null,
        // The row a 409 matched, held while the "same event?" dialog is open.
        duplicate: null,
        // The entry the last successful add produced — what a caller shows as
        // "added" without re-reading the whole record.
        lastAdded: null,
    },
    reducers: {
        clearHealthRecord(state) {
            state.patient = null;
            state.categories = emptyCategories();
            state.viewer = null;
            state.counts = emptyCounts();
            state.readings = emptyReadings();
            state.programmes = emptyProgrammes();
            state.error = null;
            state.addError = null;
            state.duplicate = null;
            state.lastAdded = null;
        },
        clearDuplicate(state) {
            state.duplicate = null;
        },
        clearAddError(state) {
            state.addError = null;
            state.lastAdded = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHealthRecord.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchHealthRecord.fulfilled, (state, action) => {
                state.loading = false;
                state.patient = action.payload?.patient || null;
                state.categories = { ...emptyCategories(), ...(action.payload?.categories || {}) };
                state.viewer = action.payload?.viewer || null;
                state.counts = {
                    readings: countOf(action.payload?.counts?.readings),
                    programmes: countOf(action.payload?.counts?.programmes),
                };
            })
            .addCase(fetchHealthRecord.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || { code: null, message: 'Request failed', status: null };
                // A refusal must not leave the previous patient's record on
                // screen — the tab is remounted per patient.
                state.patient = null;
                state.categories = emptyCategories();
                state.viewer = null;
                state.counts = emptyCounts();
                state.readings = emptyReadings();
                state.programmes = emptyProgrammes();
            })
            .addCase(fetchHealthReadings.pending, (state, action) => {
                const { patientId, appointmentId = null } = action.meta.arg;
                const same = state.readings.patientId === patientId;
                state.readings = {
                    ...(same ? state.readings : emptyReadings()),
                    patientId,
                    appointmentId,
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchHealthReadings.fulfilled, (state, action) => {
                if (state.readings.patientId !== action.meta.arg.patientId) return;
                const p = action.payload || {};
                state.readings.loading = false;
                state.readings.loaded = true;
                state.readings.visits = Array.isArray(p.visits) ? p.visits : [];
                state.readings.series = Array.isArray(p.series) ? p.series : [];
                state.readings.count = Number.isFinite(Number(p.count)) ? Number(p.count)
                    : state.readings.visits.reduce((n, v) => n + (v.readings || []).length, 0);
            })
            .addCase(fetchHealthReadings.rejected, (state, action) => {
                if (state.readings.patientId !== action.meta.arg.patientId) return;
                state.readings.loading = false;
                state.readings.loaded = true;
                state.readings.error = action.payload || { code: null, message: 'Request failed', status: null };
            })
            .addCase(fetchVisitRecordReadings.pending, (state, action) => {
                const { patientId, appointmentId } = action.meta.arg;
                if (!state.visitReadings) state.visitReadings = {};
                const prev = state.visitReadings[appointmentId];
                const same = prev && prev.patientId === patientId;
                // A re-read keeps what is on screen until the answer lands.
                state.visitReadings[appointmentId] = {
                    patientId,
                    readings: same ? prev.readings : [],
                    loaded: same ? prev.loaded : false,
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchVisitRecordReadings.fulfilled, (state, action) => {
                const { patientId, appointmentId } = action.meta.arg;
                if (!state.visitReadings) state.visitReadings = {};
                const visits = Array.isArray(action.payload?.visits) ? action.payload.visits : [];
                // Only THIS appointment's group, even from a server that
                // ignored `?visit=` and sent the whole record.
                const readings = visits
                    .filter((v) => v && String(v.appointmentId) === String(appointmentId))
                    .flatMap((v) => (Array.isArray(v.readings) ? v.readings : []));
                state.visitReadings[appointmentId] = { patientId, readings, loaded: true, loading: false, error: null };
            })
            .addCase(fetchVisitRecordReadings.rejected, (state, action) => {
                const { patientId, appointmentId } = action.meta.arg;
                if (!state.visitReadings) state.visitReadings = {};
                const prev = state.visitReadings[appointmentId];
                state.visitReadings[appointmentId] = {
                    patientId,
                    readings: prev && prev.patientId === patientId ? prev.readings : [],
                    loaded: true,
                    loading: false,
                    error: action.payload || { code: null, message: 'Request failed', status: null },
                };
            })
            .addCase(fetchHealthProgrammes.pending, (state, action) => {
                const { patientId } = action.meta.arg;
                const prev = state.programmes;
                const same = prev && prev.patientId === patientId;
                state.programmes = {
                    ...(same ? prev : emptyProgrammes()),
                    patientId,
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchHealthProgrammes.fulfilled, (state, action) => {
                if (state.programmes.patientId !== action.meta.arg.patientId) return;
                const p = action.payload || {};
                state.programmes.loading = false;
                state.programmes.loaded = true;
                state.programmes.active = Array.isArray(p.active) ? p.active : [];
                state.programmes.closed = Array.isArray(p.closed) ? p.closed : [];
            })
            .addCase(fetchHealthProgrammes.rejected, (state, action) => {
                if (state.programmes.patientId !== action.meta.arg.patientId) return;
                state.programmes.loading = false;
                state.programmes.loaded = true;
                state.programmes.error = action.payload || { code: null, message: 'Request failed', status: null };
            })
            .addCase(addHealthEntry.pending, (state) => {
                state.saving = true;
                state.addError = null;
                state.lastAdded = null;
            })
            .addCase(addHealthEntry.fulfilled, (state, action) => {
                state.saving = false;
                state.duplicate = null;
                const entry = action.payload?.entry || null;
                upsertEntry(state, entry);
                state.lastAdded = entry;
            })
            .addCase(addHealthEntry.rejected, (state, action) => {
                state.saving = false;
                const payload = action.payload || {};
                if (payload.code === 'POSSIBLE_DUPLICATE') {
                    state.duplicate = payload.existing || null;
                    return;
                }
                state.addError = payload;
            })
            .addCase(confirmHealthEntry.fulfilled, (state, action) => {
                state.saving = false;
                upsertEntry(state, action.payload?.entry || null);
            })
            .addCase(confirmHealthEntry.pending, (state) => {
                state.saving = true;
                state.addError = null;
            })
            .addCase(confirmHealthEntry.rejected, (state, action) => {
                state.saving = false;
                state.addError = action.payload || null;
            });
    },
});

export const { clearHealthRecord, clearDuplicate, clearAddError } = healthRecordSlice.actions;
export default healthRecordSlice.reducer;
